import { Bar } from 'react-chartjs-2';

import { defined, formatPrice } from '@lib/utils';
import Typography from '@components/Typography';
import { defaultTheme } from '@lib/themes';
import { Percentages } from './percentages';
import { ChartWrapper } from './style';

import type { GetTrendsTrends } from '@graphql';
import type { FC } from 'react';
import type * as chartjs from 'chart.js';
import type { ChartData } from 'react-chartjs-2';
import type { PercentageInfo } from './types';

const options: chartjs.ChartOptions = {
  layout: {
    padding: {
      top: 20,
    },
  },
  legend: {
    display: false,
  },
  scales: {
    xAxes: [
      {
        id: 'xAxis1',
        type: 'category',
        ticks: {
          minRotation: 0,
          maxRotation: 0,
          fontColor: defaultTheme.colors.primary,
          fontFamily: 'Inter',
          callback: (value: string) => {
            return value.split('-')[0];
          },
        },
        gridLines: {
          display: false,
        },
      },
      {
        id: 'xAxis2',
        type: 'category',
        ticks: {
          fontColor: defaultTheme.colors.primary,
          fontFamily: 'Inter',
          callback: (value: string) => {
            const dateArray = value.split('-');
            const [quarter, year] = dateArray;

            return quarter === 'Q3' ? year : '';
          },
        },
        gridLines: {
          display: false,
        },
      },
    ],
    yAxes: [
      {
        gridLines: {
          display: false,
        },
        ticks: {
          beginAtZero: true,
          stepSize: 1000,
          fontColor: defaultTheme.colors.primary,
          fontFamily: 'Inter',
          callback: (value) => {
            return value.toLocaleString('de-DE');
          },
        },
      },
    ],
  },
  tooltips: {
    //   enabled: false,
    callbacks: {
      label: (tooltipItem, data) => {
        if (
          !data ||
          !data.datasets ||
          !tooltipItem ||
          typeof tooltipItem.datasetIndex === 'undefined'
        ) {
          return '';
        }
        let label = data.datasets[tooltipItem.datasetIndex].label || '';

        if (tooltipItem.yLabel) {
          label += `: ${formatPrice(tooltipItem.yLabel)} €/m²`;
        }

        return label;
      },
    },
  },
};

export type ChartProps = {
  title: string;
  trends: GetTrendsTrends[];
};

const calcPercentage = (val1: number, val2: number): number => (val1 - val2) / val2;

export const Chart: FC<ChartProps> = ({ title, trends }) => {
  if (!trends) {
    return null;
  }

  const labels = trends
    .filter(defined)
    .map((trend) => `Q${trend.quarter.number}-${trend.quarter.year}`);
  const prices = trends.filter(defined).map((trend) => trend.avgPrice);
  const pricesLen = prices.length;
  const latestPrice = prices[pricesLen - 1];

  const percentages: PercentageInfo[] = [];

  if (prices[pricesLen - 2]) {
    percentages.push({
      label: '3 Monate',
      value: calcPercentage(latestPrice, prices[pricesLen - 2]),
    });
  }

  if (pricesLen > 2 && prices[pricesLen - 3]) {
    percentages.push({
      label: '6 Monate',
      value: calcPercentage(latestPrice, prices[pricesLen - 3]),
    });
  }

  if (pricesLen > 4 && prices[pricesLen - 5]) {
    percentages.push({
      label: '1 Jahr',
      value: calcPercentage(latestPrice, prices[pricesLen - 5]),
    });
  }

  const data: ChartData<chartjs.ChartData> = (canvas: HTMLElement) => {
    const ctx: CanvasRenderingContext2D | null = (canvas as HTMLCanvasElement)?.getContext('2d');
    const gradient = ctx?.createLinearGradient(0, 0, 0, 400);
    gradient?.addColorStop(0, '#005c54');
    gradient?.addColorStop(1, '#004b54');

    return {
      labels,
      datasets: [
        {
          fill: true,
          lineTension: 0.1,
          backgroundColor: gradient,
          data: prices,
        },
      ],
    };
  };

  return (
    <ChartWrapper>
      <Typography as="h2" type="H3" className="h3">
        {title}
      </Typography>
      <Percentages data={percentages} />
      <Typography type="C1" className="y-axis-label">
        €/m²
      </Typography>
      <Bar data={data} options={options} />
    </ChartWrapper>
  );
};
